import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, MessageCircle, User, Send, AlertTriangle, Phone, Calendar, ChevronRight, ChevronLeft, UserCheck, Bot, ArrowLeftRight, X, Loader2, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const statusColors = {
  active: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-100 text-slate-600",
  handoff: "bg-amber-100 text-amber-800",
  human_takeover: "bg-blue-100 text-blue-800",
};

const agentColors = {
  tanya: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", dot: "bg-violet-500" },
  jyoti: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", dot: "bg-rose-500" },
};

function ConversationDetail({ leadId, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyMsg, setReplyMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("ai"); // "ai" or "human"
  const messagesEndRef = { current: null };

  const fetchDetail = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-agent/conversations/${leadId}`, { withCredentials: true });
      setData(res.data);
      const status = res.data?.conversation?.status;
      if (status === "human_takeover") setMode("human");
      else if (status === "active") setMode("ai");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);
  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [data]);

  const handleTakeover = async () => {
    try {
      await axios.post(`${API}/ai-agent/conversations/${leadId}/takeover`, {}, { withCredentials: true });
      setMode("human");
      await fetchDetail();
      if (onRefresh) onRefresh();
    } catch (e) { console.error(e); }
  };

  const handleResumeAI = async () => {
    try {
      await axios.post(`${API}/ai-agent/conversations/${leadId}/resume-ai`, {}, { withCredentials: true });
      setMode("ai");
      await fetchDetail();
      if (onRefresh) onRefresh();
    } catch (e) { console.error(e); }
  };

  const handleSendHumanReply = async () => {
    if (!replyMsg.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/ai-agent/conversations/${leadId}/human-reply`, { message: replyMsg }, { withCredentials: true });
      setReplyMsg("");
      setTimeout(async () => { await fetchDetail(); if (onRefresh) onRefresh(); setSending(false); }, 1000);
    } catch (e) { console.error(e); setSending(false); }
  };

  const handleSendTest = async () => {
    if (!replyMsg.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/ai-agent/test-chat`, { lead_id: leadId, message: replyMsg }, { withCredentials: true });
      setReplyMsg("");
      setTimeout(async () => { await fetchDetail(); if (onRefresh) onRefresh(); setSending(false); }, 2000);
    } catch (e) { console.error(e); setSending(false); }
  };

  if (loading) return <div className="p-6 text-center text-slate-500">Loading...</div>;
  if (!data) return <div className="p-6 text-center text-red-500">Error loading conversation</div>;

  const { conversation, lead } = data;
  const messages = conversation?.messages || [];
  const agentName = conversation?.agent_name || "tanya";
  const colors = agentColors[agentName] || agentColors.tanya;
  const isHumanMode = mode === "human" || conversation?.status === "human_takeover";
  const canReply = conversation?.status !== "closed";

  return (
    <div className="flex flex-col h-full" data-testid="conversation-detail">
      <div className={`flex items-center justify-between p-4 border-b ${isHumanMode ? "bg-blue-50" : colors.bg}`}>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-800">{lead?.name || "Unknown"}</h3>
            <Badge className={`text-[10px] ${colors.text} ${colors.bg} border ${colors.border}`}>
              {agentName === "tanya" ? "Tanya" : "Jyoti"}
            </Badge>
            {isHumanMode && <Badge className="text-[10px] bg-blue-100 text-blue-800 border border-blue-200">Human Mode</Badge>}
          </div>
          <p className="text-sm text-slate-500 flex items-center gap-2">
            <Phone className="w-3 h-3" /> {lead?.phone} | {lead?.source}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={statusColors[conversation?.status] || statusColors.active}>
            {conversation?.status === "human_takeover" ? "Human Takeover" : conversation?.status}
          </Badge>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded" data-testid="close-detail">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Takeover Controls */}
      {canReply && (
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 border-b">
          {!isHumanMode ? (
            <Button size="sm" variant="outline" onClick={handleTakeover} className="text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="takeover-btn">
              <UserCheck className="w-3 h-3 mr-1" /> Take Over Chat
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={handleResumeAI} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="resume-ai-btn">
              <Bot className="w-3 h-3 mr-1" /> Resume AI
            </Button>
          )}
          <span className="text-xs text-slate-500">
            {isHumanMode ? "You are replying as " + (agentName === "tanya" ? "Tanya" : "Jyoti") + ". Customer sees your messages on WhatsApp." : "AI is handling this chat. Click 'Take Over' to reply manually."}
          </span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" style={{ maxHeight: "400px" }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "assistant" ? "justify-start" : "justify-end"}`}>
            <div className={`max-w-[80%] p-3 rounded-lg text-sm ${
              msg.role === "assistant"
                ? msg.is_human
                  ? "bg-blue-50 border border-blue-200"
                  : `${colors.bg} border ${colors.border}`
                : "bg-white border border-slate-200"
            }`}>
              <p className={`text-[10px] font-medium mb-1 ${
                msg.role === "assistant"
                  ? msg.is_human ? "text-blue-700" : colors.text
                  : "text-slate-500"
              }`}>
                {msg.role === "assistant"
                  ? msg.is_human
                    ? `${agentName === "tanya" ? "Tanya" : "Jyoti"} (Human)`
                    : (agentName === "tanya" ? "Tanya" : "Jyoti") + " (AI)"
                  : "Customer"}
              </p>
              {msg.metadata?.image_url && (
                <div className="mb-2">
                  {msg.metadata.image_base64 ? (
                    <img src={`data:image/jpeg;base64,${msg.metadata.image_base64}`} alt="Shared image" className="max-w-full rounded-md border border-slate-200 max-h-48 object-contain" />
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-slate-100 rounded-md text-xs text-slate-500 border border-slate-200">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      <a href={msg.metadata.image_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate" data-testid={`image-link-${i}`}>View shared image</a>
                    </div>
                  )}
                  {msg.metadata.image_analysis && (
                    <p className="text-[10px] text-slate-500 mt-1 italic">AI Analysis: {msg.metadata.image_analysis.substring(0, 150)}...</p>
                  )}
                </div>
              )}
              <p className="text-slate-700 whitespace-pre-wrap">{msg.content}</p>
              <p className="text-[9px] text-slate-400 mt-1">{msg.timestamp ? new Date(msg.timestamp).toLocaleString("en-IN") : ""}</p>
            </div>
          </div>
        ))}
        <div ref={(el) => { messagesEndRef.current = el; }} />
      </div>

      {canReply && (
        <div className="p-3 border-t bg-white">
          <div className="flex gap-2">
            <input
              value={replyMsg}
              onChange={(e) => setReplyMsg(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (isHumanMode ? handleSendHumanReply() : handleSendTest())}
              placeholder={isHumanMode ? "Type your reply to customer..." : "Type as customer to test AI..."}
              className={`flex-1 px-3 py-2 text-sm border rounded-full focus:outline-none focus:ring-2 ${isHumanMode ? "focus:ring-blue-400 border-blue-200" : "focus:ring-cyan-400"}`}
              disabled={sending}
              data-testid="reply-input"
            />
            <button
              onClick={isHumanMode ? handleSendHumanReply : handleSendTest}
              disabled={sending || !replyMsg.trim()}
              className={`p-2 text-white rounded-full disabled:opacity-50 transition ${isHumanMode ? "bg-blue-600 hover:bg-blue-700" : "bg-cyan-600 hover:bg-cyan-700"}`}
              data-testid="reply-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1 text-center">
            {isHumanMode
              ? "Human mode: Your message is sent directly to customer's WhatsApp."
              : "Test mode: Type as customer. AI responds via real WhatsApp."}
          </p>
        </div>
      )}
    </div>
  );
}

/* ==================== BOOKINGS TAB ==================== */
function BookingsTab({ source }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/ai-agent/bookings?source=${source}`, { withCredentials: true });
        setBookings(res.data.bookings || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [source]);

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div data-testid="bookings-tab">
      {bookings.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Calendar className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>No bookings yet</p>
          <p className="text-xs mt-1">AI agent will book appointments when customers confirm</p>
        </div>
      ) : (
        <div className="space-y-2">
          {bookings.map((b) => {
            const agent = agentColors[b.agent] || agentColors.tanya;
            return (
              <Card key={b.task_id} className={`cursor-pointer hover:shadow-md transition ${selectedBooking === b.task_id ? "ring-2 ring-cyan-500" : ""}`}
                onClick={() => setSelectedBooking(selectedBooking === b.task_id ? null : b.task_id)} data-testid={`booking-${b.task_id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${agent.bg}`}>
                        <Calendar className={`w-4 h-4 ${agent.text}`} />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-800">{b.lead?.name || "Unknown"}</p>
                        <p className="text-xs text-slate-400">{b.lead?.phone} | {b.agent || "AI"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-cyan-100 text-cyan-700 text-[10px]">{b.appointment_type || "Clinic Visit"}</Badge>
                      {b.is_eligible && <Badge variant="outline" className="text-[10px]">Past Patient</Badge>}
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1 pl-10">
                    {b.created_at ? new Date(b.created_at).toLocaleString("en-IN") : ""}
                    {b.lead?.city && ` | ${b.lead.city}`}
                    {b.lead?.source && ` | ${b.lead.source}`}
                  </p>
                  {selectedBooking === b.task_id && (
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg text-xs space-y-1 border">
                      <p><span className="font-medium text-slate-600">Lead ID:</span> {b.lead_id}</p>
                      <p><span className="font-medium text-slate-600">Agent:</span> {b.agent}</p>
                      <p><span className="font-medium text-slate-600">Status:</span> {b.status}</p>
                      <p><span className="font-medium text-slate-600">Type:</span> {b.appointment_type}</p>
                      {b.details && Object.keys(b.details).length > 0 && (
                        <div>
                          <span className="font-medium text-slate-600">Details:</span>
                          <pre className="bg-white p-2 rounded text-[10px] mt-1 overflow-x-auto">{JSON.stringify(b.details, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ==================== DRILL-DOWN MODAL ==================== */
function DrillDownModal({ title, conversations, onClose, onSelectConv }) {
  if (!conversations) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[70vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-slate-800">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto max-h-[55vh] p-2 space-y-1">
          {conversations.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No conversations</p>}
          {conversations.map((c) => {
            const colors = agentColors[c.agent_name] || agentColors.tanya;
            return (
              <div key={c.lead_id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-200"
                onClick={() => { onSelectConv(c.lead_id); onClose(); }} data-testid={`drill-${c.lead_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-7 h-7 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                    <span className={`text-xs font-bold ${colors.text}`}>{(c.agent_name || "T")[0].toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{c.lead_name || "Unknown"}</p>
                    <p className="text-xs text-slate-400">{c.lead_phone}</p>
                  </div>
                </div>
                <Badge className={`text-[10px] ${statusColors[c.status] || ""}`}>{c.status}</Badge>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function AIAgentDashboard({ user, logout }) {
  const [stats, setStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeTab, setActiveTab] = useState("conversations"); // conversations | bookings
  const [drillDown, setDrillDown] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalConvs, setTotalConvs] = useState(0);
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let convsUrl = `${API}/ai-agent/conversations?page=${page}&limit=50`;
      if (filter !== "all") convsUrl += `&status=${filter}`;
      if (agentFilter !== "all") convsUrl += `&agent=${agentFilter}`;
      if (search) convsUrl += `&search=${encodeURIComponent(search)}`;

      const [statsRes, convsRes] = await Promise.all([
        axios.get(`${API}/ai-agent/stats`, { withCredentials: true }),
        axios.get(convsUrl, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setConversations(convsRes.data.conversations || []);
      setTotalPages(convsRes.data.pages || 1);
      setTotalConvs(convsRes.data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filter, agentFilter, page, search]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleClose = async (leadId) => {
    try {
      await axios.post(`${API}/ai-agent/conversations/${leadId}/close`, {}, { withCredentials: true });
      fetchData();
    } catch (e) { console.error(e); }
  };

  // Drill-down handler
  const handleStatClick = async (statFilter) => {
    try {
      let url = `${API}/ai-agent/conversations?limit=50`;
      if (statFilter !== "all" && statFilter !== "total") url += `&status=${statFilter}`;
      const res = await axios.get(url, { withCredentials: true });
      const titles = { total: "All Conversations", active: "Active Conversations", handoff: "Handoff Conversations", human_takeover: "Human Takeover", closed: "Closed Conversations" };
      setDrillDown({ title: titles[statFilter] || statFilter, conversations: res.data.conversations || [] });
    } catch (e) { console.error(e); }
  };

  const tanyaStats = stats?.by_agent?.tanya || {};
  const jyotiStats = stats?.by_agent?.jyoti || {};

  return (
    <DashboardLayout user={user} logout={logout} activeTab="ai-agent">
      <div className="p-6 space-y-6" data-testid="ai-agent-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">AI WhatsApp Agents</h1>
            <p className="text-sm text-slate-500">Tanya & Jyoti — Automated lead engagement via WhatsApp</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* Agent Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="agent-cards">
            <div
              className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                agentFilter === "tanya" ? "border-violet-500 bg-violet-50 shadow-md" : "border-violet-100 bg-white hover:border-violet-300"
              }`}
              onClick={() => setAgentFilter(agentFilter === "tanya" ? "all" : "tanya")}
              data-testid="agent-tanya-card"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center text-white font-bold text-lg">T</div>
                <div>
                  <h3 className="font-semibold text-violet-800">Tanya</h3>
                  <p className="text-xs text-violet-500">AI Sales Agent</p>
                </div>
                {tanyaStats.active > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700 ml-auto">{tanyaStats.active} Active</Badge>
                )}
              </div>
              <div className="flex gap-4 text-sm">
                <span className="text-violet-700 font-medium">{tanyaStats.total || 0} Total</span>
                <span className="text-emerald-600">{tanyaStats.active || 0} Active</span>
              </div>
            </div>
            <div
              className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                agentFilter === "jyoti" ? "border-rose-500 bg-rose-50 shadow-md" : "border-rose-100 bg-white hover:border-rose-300"
              }`}
              onClick={() => setAgentFilter(agentFilter === "jyoti" ? "all" : "jyoti")}
              data-testid="agent-jyoti-card"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-rose-500 flex items-center justify-center text-white font-bold text-lg">J</div>
                <div>
                  <h3 className="font-semibold text-rose-800">Jyoti</h3>
                  <p className="text-xs text-rose-500">AI Sales Agent</p>
                </div>
                {jyotiStats.active > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700 ml-auto">{jyotiStats.active} Active</Badge>
                )}
              </div>
              <div className="flex gap-4 text-sm">
                <span className="text-rose-700 font-medium">{jyotiStats.total || 0} Total</span>
                <span className="text-emerald-600">{jyotiStats.active || 0} Active</span>
              </div>
            </div>
          </div>
        )}

        {/* Overview Stats (clickable) */}
        {stats && (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="stats-grid">
            {[
              { key: "total", label: "Total Chats", value: stats.total, color: "text-cyan-700", clickFilter: "total" },
              { key: "active", label: "Active", value: stats.active, color: "text-emerald-600", clickFilter: "active" },
              { key: "handoffs", label: "Handoffs", value: stats.handoffs_total || stats.handoffs, color: "text-amber-600", clickFilter: "handoff" },
              { key: "bookings", label: "Bookings", value: stats.bookings || 0, color: "text-blue-600", clickFilter: null },
              { key: "closed", label: "Closed", value: stats.closed, color: "text-slate-600", clickFilter: "closed" },
              { key: "today", label: "Today", value: stats.today, color: "text-purple-600", clickFilter: null },
            ].map((s) => (
              <Card key={s.key} className={`${s.clickFilter ? "cursor-pointer hover:shadow-md hover:border-slate-300 transition" : ""}`}
                onClick={() => s.clickFilter && handleStatClick(s.clickFilter)} data-testid={`stat-${s.key}`}>
                <CardContent className="p-3 text-center">
                  <p className={`text-xl font-bold ${s.color}`}>{s.value || 0}</p>
                  <p className="text-[10px] text-slate-500">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Tabs: Conversations | Bookings Done */}
        <div className="flex gap-1 border-b" data-testid="main-tabs">
          <button onClick={() => setActiveTab("conversations")}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition ${activeTab === "conversations" ? "border-cyan-600 text-cyan-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-conversations">
            <MessageCircle className="w-4 h-4" /> Conversations
          </button>
          <button onClick={() => setActiveTab("bookings")}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition ${activeTab === "bookings" ? "border-cyan-600 text-cyan-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-bookings">
            <Calendar className="w-4 h-4" /> Bookings Done
          </button>
        </div>

        {/* Bookings Tab */}
        {activeTab === "bookings" && <BookingsTab source="new_leads" />}

        {/* Conversations Tab */}
        {activeTab === "conversations" && (
          <>
            {/* Status Filter Tabs + Search */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="filter-tabs">
              <div className="flex gap-2 flex-wrap">
                {["all", "active", "human_takeover", "handoff", "closed"].map((f) => (
                  <button
                    key={f}
                    onClick={() => { setFilter(f); setPage(1); }}
                    className={`px-3 py-1.5 text-sm rounded-full border transition ${
                      filter === f ? "bg-cyan-700 text-white border-cyan-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`filter-${f}`}
                  >
                    {f === "all" ? "All" : f === "human_takeover" ? "Human Takeover" : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
                {agentFilter !== "all" && (
                  <button
                    onClick={() => { setAgentFilter("all"); setPage(1); }}
                    className="px-3 py-1.5 text-sm rounded-full border bg-slate-100 text-slate-600 hover:bg-slate-200 transition"
                    data-testid="clear-agent-filter"
                  >
                    Clear agent filter
                  </button>
                )}
              </div>
              <div className="relative flex-1 max-w-xs ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search phone number..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-9 h-9"
                  data-testid="conversation-search"
                />
              </div>
            </div>

            {/* Pagination info */}
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Showing {conversations.length} of {totalConvs.toLocaleString()} conversations (Page {page}/{totalPages})</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">{page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ minHeight: "500px" }}>
              <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="conversation-list">
                {conversations.length === 0 && !loading && (
                  <div className="text-center py-12 text-slate-400">
                    <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p>No conversations found</p>
                    <p className="text-xs mt-1">AI agent will start conversations when new leads arrive</p>
                  </div>
                )}
                {conversations.map((conv) => {
                  const lastMsg = conv.messages?.[conv.messages.length - 1];
                  const agentName = conv.agent_name || "tanya";
                  const colors = agentColors[agentName] || agentColors.tanya;
                  return (
                    <Card
                      key={conv.lead_id}
                      className={`cursor-pointer hover:shadow-md transition ${selectedLead === conv.lead_id ? "ring-2 ring-cyan-500" : ""}`}
                      onClick={() => setSelectedLead(conv.lead_id)}
                      data-testid={`conv-${conv.lead_id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className={`w-8 h-8 rounded-full ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                              <span className={`text-sm font-bold ${colors.text}`}>{agentName[0].toUpperCase()}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-slate-800 truncate">{conv.lead_name || "Unknown"}</p>
                              <p className="text-xs text-slate-400 truncate">{conv.lead_phone} | {conv.lead_source}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge className={`text-[10px] ${statusColors[conv.status] || statusColors.active}`}>
                              {conv.status === "human_takeover" ? "Human" : conv.status}
                            </Badge>
                            {conv.status === "handoff" && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                            {conv.status === "human_takeover" && <UserCheck className="w-4 h-4 text-blue-500" />}
                          </div>
                        </div>
                        {lastMsg && (
                          <p className="text-xs text-slate-500 mt-2 truncate pl-10">
                            {lastMsg.role === "assistant" ? `${agentName === "tanya" ? "Tanya" : "Jyoti"}: ` : "Customer: "}
                            {lastMsg.content?.substring(0, 80)}...
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-1 pl-10">
                          <span className={`text-[10px] font-medium ${colors.text}`}>
                            {agentName === "tanya" ? "Tanya" : "Jyoti"}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {conv.updated_at ? new Date(conv.updated_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <Card className="overflow-hidden" style={{ minHeight: "500px" }}>
                {selectedLead ? (
                  <ConversationDetail leadId={selectedLead} onClose={() => setSelectedLead(null)} onRefresh={fetchData} />
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-400">
                    <div className="text-center">
                      <Send className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Select a conversation to view details</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </>
        )}
      </div>

      {/* Drill-down modal */}
      {drillDown && <DrillDownModal title={drillDown.title} conversations={drillDown.conversations} onClose={() => setDrillDown(null)} onSelectConv={(id) => { setActiveTab("conversations"); setSelectedLead(id); }} />}
    </DashboardLayout>
  );
}
